import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { CornerDownLeft, Search } from "lucide-react";

import {
  collectPageFieldTargets,
  filterPageFieldTargets,
  isFieldSearchShortcut,
  jumpToPageField,
  type PageFieldTarget,
} from "@/lib/pageFieldSearch";
import { FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

const MAX_VISIBLE_FIELDS = 80;

export function FieldSearcher() {
  const { pathname } = useLocation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [pageFields, setPageFields] = useState<PageFieldTarget[]>([]);

  const refreshPageFields = useCallback(() => {
    const main = document.querySelector("main");
    if (!(main instanceof HTMLElement)) {
      setPageFields([]);
      return;
    }
    setPageFields(collectPageFieldTargets(main));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: Event) => {
      if (!(event instanceof globalThis.KeyboardEvent)) return;
      if (!isFieldSearchShortcut(event)) return;
      event.preventDefault();
      refreshPageFields();
      setOpen(true);
      window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [refreshPageFields]);

  useEffect(() => {
    if (!open) return;
    refreshPageFields();
  }, [open, pathname, refreshPageFields]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, open, pathname]);

  const filteredFields = useMemo(
    () => filterPageFieldTargets(pageFields, query).slice(0, MAX_VISIBLE_FIELDS),
    [pageFields, query],
  );

  useEffect(() => {
    setHighlightedIndex((previous) =>
      filteredFields.length === 0 ? 0 : Math.min(previous, filteredFields.length - 1),
    );
  }, [filteredFields.length]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  const selectField = useCallback(
    (target: PageFieldTarget) => {
      close();
      jumpToPageField(target);
    },
    [close],
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (panelRef.current?.contains(event.target as Node)) return;
      close();
    };
    const onKeyDown = (event: Event) => {
      if (!(event instanceof globalThis.KeyboardEvent)) return;
      if (event.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close, open]);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [open]);

  const onListKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (filteredFields.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, filteredFields.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const target = filteredFields[highlightedIndex];
      if (target) selectField(target);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-start justify-center bg-slate-900/20 px-4 pt-[14vh]"
      style={{ zIndex: FLOATING_POPOVER_Z_INDEX }}
      role="presentation"
    >
      <div
        ref={panelRef}
        className="flex w-full max-w-xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
        role="dialog"
        aria-label="Search fields on this page"
      >
        <label className="block shrink-0 border-b border-slate-100 px-3 py-2">
          <span className="sr-only">Search fields on this page</span>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onListKeyDown}
              placeholder="Search fields on this page…"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              className={cn(
                "w-full rounded-md border border-slate-200 py-2 pl-9 pr-20 text-sm",
                "text-slate-900 placeholder:text-slate-400",
                "focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
              )}
            />
            <span className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 text-[10px] text-slate-400">
              <CornerDownLeft className="h-3 w-3" aria-hidden /> enter
            </span>
          </div>
        </label>

        <div
          className="max-h-[min(24rem,50vh)] overflow-y-auto py-2"
          role="listbox"
          aria-activedescendant={
            filteredFields[highlightedIndex] ? `field-search-${highlightedIndex}` : undefined
          }
        >
          {filteredFields.length > 0 ? (
            <ul className="space-y-0.5 px-1">
              {filteredFields.map((field, index) => {
                const active = highlightedIndex === index;
                return (
                  <li key={field.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      id={`field-search-${index}`}
                      aria-selected={active}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => selectField(field)}
                      className={cn(
                        "flex w-full flex-col rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                        active ? "bg-brand-50 text-brand-900" : "text-slate-700 hover:bg-slate-50",
                      )}
                    >
                      <span className="font-medium">{field.label}</span>
                      {field.section ? (
                        <span className="truncate text-xs text-slate-500">{field.section}</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : query.trim().length > 0 ? (
            <p className="px-4 py-3 text-center text-sm text-slate-500">No fields match on this page.</p>
          ) : (
            <p className="px-4 py-3 text-center text-sm text-slate-500">
              No searchable fields were found on this page.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
